import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Package, ArrowRight, Clock, EnvelopeSimple, User } from "@phosphor-icons/react";
import api, { money } from "@/lib/api";
import SEO from "@/components/SEO";

export default function CheckoutSuccess() {
  const [sp] = useSearchParams();
  const location = useLocation();
  const orderNumber = sp.get("order");
  const [order, setOrder] = useState(location.state?.order || null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (order) return;
    if (!orderNumber) { setNotFound(true); return; }
    api.get(`/orders/lookup/${orderNumber}`).then((r) => setOrder(r.data)).catch(() => setNotFound(true));
    // eslint-disable-next-line
  }, []);

  const cur = (order?.currency || "GBP").toUpperCase();
  const fx = order?.fx_rate || 1;
  const m = (gbpAmount) => money((gbpAmount || 0) * fx, cur);
  const ship = order?.shipping || {};

  return (
    <>
      <SEO title="Order Confirmation" description="Your Ebikes&Gear order confirmation." path="/checkout/success" />
      <div className="container-px py-20">
        <div className="mx-auto max-w-2xl rounded-[2rem] border border-border bg-card p-8 sm:p-12 shadow-soft" data-testid="checkout-status">
          {notFound && (
            <div className="text-center">
              <XCircle size={72} weight="fill" className="mx-auto text-primary" data-testid="order-not-found" />
              <h1 className="mt-6 text-2xl font-black">Order not found</h1>
              <p className="mt-2 text-muted-foreground">We couldn't find that order. Please check your email for confirmation or contact us.</p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/cart" className="btn-primary">Return to cart <ArrowRight size={18} /></Link>
                <Link to="/contact" className="btn-outline">Contact support</Link>
              </div>
            </div>
          )}
          {!notFound && !order && (
            <div className="text-center">
              <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary" />
              <h1 className="mt-6 text-2xl font-black">Loading your order…</h1>
            </div>
          )}
          {order && (
            <>
              <div className="text-center">
                <CheckCircle size={72} weight="fill" className="mx-auto text-green-500" data-testid="order-confirmed" />
                <h1 className="mt-6 text-3xl font-black">Thank you for your order<br />#{order.order_number}</h1>
                <p className="mt-2 text-sm text-muted-foreground" data-testid="order-date">
                  Order date: {new Date(order.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </p>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-4 py-2 text-sm font-bold text-amber-600" data-testid="reservation-notice">
                  <Clock size={16} weight="fill" /> Your order is reserved for 48 hours.
                </div>
                <p className="mt-4 flex items-center justify-center gap-1.5 text-sm text-muted-foreground" data-testid="payment-followup-note">
                  <EnvelopeSimple size={16} /> A confirmation email is on its way — our team will follow up shortly with everything you need to complete your order.
                </p>
              </div>

              {/* Customer information */}
              <div className="mt-8 rounded-2xl border border-border bg-background p-5" data-testid="customer-details">
                <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-muted-foreground"><User size={16} /> Your details</h2>
                <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                  <div className="flex justify-between sm:block"><dt className="text-muted-foreground">Name</dt><dd className="font-semibold" data-testid="customer-name">{ship.name}</dd></div>
                  <div className="flex justify-between sm:block"><dt className="text-muted-foreground">Email</dt><dd className="font-semibold" data-testid="customer-email">{order.email}</dd></div>
                  <div className="flex justify-between sm:block"><dt className="text-muted-foreground">Street address</dt><dd className="font-semibold" data-testid="customer-address">{ship.address}</dd></div>
                  <div className="flex justify-between sm:block"><dt className="text-muted-foreground">ZIP / Postal code</dt><dd className="font-semibold" data-testid="customer-postcode">{ship.postcode}</dd></div>
                  <div className="flex justify-between sm:block"><dt className="text-muted-foreground">City</dt><dd className="font-semibold" data-testid="customer-city">{ship.city}</dd></div>
                  <div className="flex justify-between sm:block"><dt className="text-muted-foreground">Country</dt><dd className="font-semibold" data-testid="customer-country">{ship.country}</dd></div>
                </dl>
              </div>

              {/* Order summary */}
              <div className="mt-5 rounded-2xl border border-border bg-background p-5" data-testid="order-summary">
                <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-muted-foreground"><Package size={16} /> Order summary</h2>
                <div className="mt-3 space-y-3">
                  {order.items?.map((it) => (
                    <div key={it.slug} className="flex items-center gap-3 text-sm" data-testid={`order-item-${it.slug}`}>
                      <img src={it.thumbnail} alt={it.name} className="h-11 w-11 rounded-lg object-cover" loading="lazy" />
                      <div className="flex-1">
                        <p className="font-semibold leading-tight">{it.name}</p>
                        <p className="text-xs text-muted-foreground">Qty {it.quantity} × {m(it.unit_price)}</p>
                      </div>
                      <span className="font-semibold">{m(it.unit_price * it.quantity)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 space-y-2 border-t border-border pt-3 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold" data-testid="confirm-subtotal">{m(order.amount)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span className="font-semibold text-green-600" data-testid="confirm-shipping">Free</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Taxes (VAT)</span><span className="font-semibold" data-testid="confirm-taxes">Included</span></div>
                  <div className="flex justify-between border-t border-border pt-2 text-base font-black"><span>Grand Total</span><span data-testid="order-total">{m(order.amount)}</span></div>
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/account" className="btn-primary" data-testid="view-orders-link"><Package size={18} /> View my orders</Link>
                <Link to="/shop" className="btn-outline">Continue shopping</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
